import { useState } from 'react';
import type { ReactNode } from 'react';
import { useAction, useApi } from '../../context';
import { Confirm, Failure, Loading } from '../../bits';
import { Chip, Panel, useToast } from '../../kit';
import { useLocale, useT } from '../../../messages';
import type {
  BrandingHistoryRow,
  BrandingRead,
  IntakeDraft,
  IntakeRead,
  PageTemplate,
} from '../../../wire';
import type { ApiFailure } from '../../../api/errors';
import { SiteDialog } from './SiteDialog';
import { StartFromAnswers } from './StartFromAnswers';
import { personOf, shortDate } from './words';

/**
 * **Their page** — what is published, and the doors into the editor.
 *
 * The page editor itself is not on this tab any more: it opens on its own
 * screen (`#/b/<id>/site/editor`) and Phase C replaces it with the visual
 * builder. This card is the summary that stays: the published version, when
 * and by whom, the note it was published with, a small picture of it, and four
 * ways in —
 *
 *  * **Open the page editor** — the stored page, as it is;
 *  * **Start from their answers** — the intake → draft hand-off (D5), a read;
 *  * **Choose a template** — a template's document handed to the editor, the
 *    same door its own picker uses;
 *  * **Restore a version** — the rollback, confirmed in words, because it is a
 *    publish: every guest sees it on their next page load.
 *
 * Each of the first three hands the editor a starting point and opens it;
 * nothing is written until Publish there.
 */
export function PageCard({
  tenantId,
  read,
  loading,
  failure,
  intake,
  onReload,
  onOpenEditor,
  onStartPage,
  onChooseTemplate,
}: {
  tenantId: string;
  read: BrandingRead | null;
  loading: boolean;
  failure: ApiFailure | null;
  /** The questionnaire, when there is one — "Start from their answers" needs it. */
  intake: IntakeRead | null;
  onReload: () => void;
  onOpenEditor: () => void;
  onStartPage: (draft: IntakeDraft) => void;
  onChooseTemplate: (template: PageTemplate) => void;
}): ReactNode {
  const t = useT();
  const locale = useLocale();
  const [choosing, setChoosing] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const published = read !== null && read.branding_version > 0;
  const templates = read?.contract.templates ?? [];
  const answered =
    intake !== null && (intake.progress.answered > 0 || intake.submitted_at !== null);

  return (
    <Panel
      title={t('site.page.title')}
      className="site-card"
      actions={
        read === null ? undefined : published ? (
          <Chip tone="ok">{t('site.page.published', { version: read.branding_version })}</Chip>
        ) : (
          <Chip>{t('site.page.notPublished')}</Chip>
        )
      }
    >
      {failure !== null ? <Failure failure={failure} /> : null}
      {read === null ? (
        loading ? (
          <Loading what={t('loading.theme')} />
        ) : null
      ) : (
        <>
          <Thumb read={read} />

          {published ? (
            <dl className="site-kv">
              <dt>{t('site.page.when')}</dt>
              <dd>
                {t('site.page.whenBy', {
                  day: shortDate(read.published_at, locale),
                  who: personOf(read.published_by) ?? t('site.page.someone'),
                })}
              </dd>
              {read.publish_note === '' ? null : (
                <>
                  <dt>{t('site.page.note')}</dt>
                  <dd>“{read.publish_note}”</dd>
                </>
              )}
            </dl>
          ) : (
            <p className="muted">{t('site.page.never')}</p>
          )}

          <div className="row-actions">
            {answered && !published && intake !== null ? (
              <StartFromAnswers
                tenantId={tenantId}
                template={intake.template}
                templates={templates}
                primary
                onStartPage={onStartPage}
              />
            ) : null}
            <button
              type="button"
              className={`button button--sm${published || !answered ? ' button--primary' : ''}`}
              onClick={onOpenEditor}
            >
              {t('site.page.open')}
            </button>
            {answered && published && intake !== null ? (
              <StartFromAnswers
                tenantId={tenantId}
                template={intake.template}
                templates={templates}
                onStartPage={onStartPage}
              />
            ) : null}
            {templates.length === 0 ? null : (
              <button type="button" className="button button--sm" onClick={() => setChoosing(true)}>
                {t('site.page.choose')}
              </button>
            )}
            {read.history.length === 0 ? null : (
              <button
                type="button"
                className="button button--quiet button--sm"
                onClick={() => setRestoring(true)}
              >
                {t('site.page.restore')}
              </button>
            )}
          </div>

          {choosing ? (
            <ChooseTemplate
              templates={templates}
              onPick={(template) => {
                setChoosing(false);
                onChooseTemplate(template);
              }}
              onClose={() => setChoosing(false)}
            />
          ) : null}

          {restoring ? (
            <Restore
              tenantId={tenantId}
              history={read.history}
              onDone={() => {
                setRestoring(false);
                onReload();
              }}
              onClose={() => setRestoring(false)}
            />
          ) : null}
        </>
      )}
    </Panel>
  );
}

/**
 * A small picture of the page — its title and tagline over a hero band — or
 * the placeholder the mockup draws when there is nothing yet. Deliberately not
 * a rendering: the real one is the editor's (and Phase C's, the site itself).
 */
function Thumb({ read }: { read: BrandingRead }): ReactNode {
  const t = useT();
  const title = read.branding.site_title ?? '';
  const tagline = read.branding.tagline ?? '';
  const empty = read.branding_version === 0 && title === '';
  return (
    <div className="site-thumb" aria-hidden="true">
      <div className="site-thumb__bar" style={{ width: '40%' }} />
      <div className="site-thumb__hero">
        {empty ? (
          t('site.page.placeholder')
        ) : (
          <>
            <strong>{title === '' ? read.preview.name : title}</strong>
            {tagline === '' ? null : <span>{tagline}</span>}
          </>
        )}
      </div>
      <div className="site-thumb__bar" />
      <div className="site-thumb__bar" style={{ width: '70%' }} />
    </div>
  );
}

function ChooseTemplate({
  templates,
  onPick,
  onClose,
}: {
  templates: readonly PageTemplate[];
  onPick: (template: PageTemplate) => void;
  onClose: () => void;
}): ReactNode {
  const t = useT();
  return (
    <SiteDialog title={t('site.page.chooseTitle')} wide onClose={onClose}>
      <p className="modal__consequence">{t('site.page.chooseLead')}</p>
      <ul className="site-templates">
        {templates.map((template) => (
          <li key={template.name}>
            <div>
              <strong>{template.label}</strong> <span className="muted">· {template.vertical}</span>
            </div>
            <p className="muted">{template.blurb}</p>
            <button
              type="button"
              className="button button--sm"
              aria-label={t('site.page.useLabel', { label: template.label })}
              onClick={() => onPick(template)}
            >
              {t('site.page.use')}
            </button>
          </li>
        ))}
      </ul>
      <div className="modal__actions">
        <button type="button" className="button" onClick={onClose}>
          {t('common.cancel')}
        </button>
      </div>
    </SiteDialog>
  );
}

/**
 * The kept versions, newest first; choosing one asks once more, in words,
 * because restoring is publishing — older bytes, as a new and higher version.
 */
function Restore({
  tenantId,
  history,
  onDone,
  onClose,
}: {
  tenantId: string;
  history: readonly BrandingHistoryRow[];
  onDone: () => void;
  onClose: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const locale = useLocale();
  const toast = useToast();
  const action = useAction();
  const [version, setVersion] = useState<number | null>(null);

  const restore = async (): Promise<void> => {
    if (version === null) return;
    let restored = 0;
    const ok = await action.run(async () => {
      restored = (await api.rollbackBranding(tenantId, version)).branding_version;
    });
    if (ok) {
      toast(t('site.restore.done', { from: version, version: restored }));
      onDone();
    }
  };

  if (version !== null) {
    return (
      <Confirm
        tone="primary"
        title={t('site.restore.title', { version })}
        consequence={t('site.restore.consequence', { version })}
        confirmLabel={t('site.restore.label', { version })}
        busy={action.busy}
        failure={action.failure}
        onConfirm={() => void restore()}
        onCancel={() => {
          action.clear();
          setVersion(null);
        }}
      />
    );
  }

  return (
    <SiteDialog title={t('site.restore.pick')} onClose={onClose}>
      <ul className="site-versions">
        {history.map((entry) => (
          <li key={entry.version}>
            <div>
              <strong>{t('site.restore.version', { version: entry.version })}</strong>{' '}
              <span className="muted">
                {t('site.page.whenBy', {
                  day: shortDate(entry.at, locale),
                  who: personOf(entry.by) ?? t('site.page.someone'),
                })}
              </span>
              {entry.note === '' ? null : <div className="muted">“{entry.note}”</div>}
            </div>
            <button
              type="button"
              className="button button--sm"
              aria-label={t('site.restore.label', { version: entry.version })}
              onClick={() => setVersion(entry.version)}
            >
              {t('site.restore.choose')}
            </button>
          </li>
        ))}
      </ul>
      <div className="modal__actions">
        <button type="button" className="button" onClick={onClose}>
          {t('common.cancel')}
        </button>
      </div>
    </SiteDialog>
  );
}
